import axios from "axios";
import { OpenAPI } from "@propertelligent/client-api";

/**
 * Thin local client for /api/UnitListingDetail endpoints.
 * Kept here until the backend CI regenerates @propertelligent/client-api.
 * Reuses OpenAPI.BASE / TOKEN so it points at the same origin + JWT as every other call.
 */

export interface UnitListingDetailItemVM {
  id: number;
  unitId: number;
  rent: number;
  leaseApplicationTemplateId: number | null;
  description: string;
  isActive: boolean;
  images?: DocumentVM[];
}

export interface DocumentVM {
  id: number;
  filePath: string;
  fileName: string;
  isActive: boolean;
}

export interface CreateUpdateUnitListingDetailVM {
  id?: number;
  unitId: number;
  rent: number;
  leaseApplicationTemplateId?: number | null;
  description: string;
  /** Document IDs in display order — persisted as Document.SortOrder on update. */
  orderedImageIds?: number[];
}

interface SingleItemResponse<T> {
  success?: T;
  errors?: Record<string, string[]>;
}

const baseUrl = (): string => (typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : "");

const authHeaders = (): Record<string, string> => {
  const token = typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : undefined;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getUnitListingDetailByUnitId = async (
  unitId: number
): Promise<SingleItemResponse<UnitListingDetailItemVM>> => {
  const res = await axios.get(`${baseUrl()}/api/UnitListingDetail/unit/${unitId}`);
  return res.data;
};

export const createUnitListingDetail = async (
  vm: CreateUpdateUnitListingDetailVM
): Promise<SingleItemResponse<CreateUpdateUnitListingDetailVM>> => {
  const res = await axios.post(`${baseUrl()}/api/UnitListingDetail`, vm, { headers: authHeaders() });
  return res.data;
};

export const updateUnitListingDetail = async (
  id: number,
  vm: CreateUpdateUnitListingDetailVM
): Promise<SingleItemResponse<CreateUpdateUnitListingDetailVM>> => {
  const res = await axios.put(`${baseUrl()}/api/UnitListingDetail/${id}`, vm, { headers: authHeaders() });
  return res.data;
};

export const toggleUnitListingDetailStatus = async (
  id: number
): Promise<SingleItemResponse<boolean>> => {
  const res = await axios.patch(`${baseUrl()}/api/UnitListingDetail/${id}/toggleStatus`, {}, { headers: authHeaders() });
  return res.data;
};
